/**
 * SHELL — streak tracker screen. Loss-aversion is the core retention hook, so
 * this screen makes the streak feel valuable and fragile: a big number, the
 * personal best, a 7-day visualization, an at-risk warning with a countdown, and
 * a one-tap daily-reminder toggle.
 */

import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el } from '../dom';
import { brand, streakBadge, banner, iconButton } from '../components';
import { formatCountdown, msUntilUtcMidnight } from '../format';
import { isStreakAtRisk } from '../../storage/local';
import { enableDailyReminder, disableDailyReminder, reminderEnabled, shouldOfferReminders } from '../../notify/daily';
import { toast } from '../toast';

export function streakScreen(app: App): Screen {
  const snap = app.store.get();
  const screen = div('dr-screen');
  const card = div('dr-card');

  card.append(brand());
  const atRisk = isStreakAtRisk(snap.dateKey, snap.streak);
  card.append(streakBadge(snap.streak.current, { atRisk, best: snap.streak.best }));

  // Seven-day mini calendar (visual, local cache based).
  card.append(buildWeekStrip(snap.streak.lastPlayedDay, snap.dateKey, snap.dailyStatus === 'completed'));

  let riskTimer = 0;
  if (atRisk) {
    const warnBanner = banner('err', '');
    const warnText = warnBanner.querySelector('span:last-child') as HTMLElement;
    warnBanner.setAttribute('aria-live', 'polite');
    const update = (): void => {
      warnText.textContent = `Streak resets in ${formatCountdown(msUntilUtcMidnight())} — play today to keep it.`;
    };
    update();
    riskTimer = window.setInterval(update, 30_000);
    card.append(warnBanner);
    screen.append(card);
  } else {
    card.append(el('p', { class: 'dr-muted', text: snap.streak.current > 0 ? 'Keep showing up. Every day adds to the chain.' : 'Finish a run to start your streak.' }));
    screen.append(card);
  }

  // Daily reminder toggle.
  const reminderCard = div('dr-card');
  reminderCard.append(el('h2', { class: 'dr-h', text: 'Daily reminder' }));
  const enabled = reminderEnabled();
  reminderCard.append(
    el('p', { class: 'dr-muted', text: enabled ? "We'll nudge you before the day rolls over." : 'Get a gentle nudge each day so a streak never slips.' }),
  );
  const reminderBtn = iconButton(
    enabled ? 'dr-btn dr-btn--ghost dr-btn--block' : 'dr-btn dr-btn--primary dr-btn--block',
    'bell',
    enabled ? 'Turn off reminders' : 'Remind me daily',
    () => void toggleReminder(),
  );
  if (!enabled && !shouldOfferReminders()) {
    // Permission was previously denied at the OS level.
    reminderBtn.disabled = false;
  }
  reminderCard.append(reminderBtn);
  screen.append(reminderCard);

  screen.append(
    div('dr-btn-row',
      snap.dailyStatus !== 'completed'
        ? iconButton('dr-btn dr-btn--primary', 'play', 'Play now', () => void app.startRun())
        : iconButton('dr-btn dr-btn--ghost', 'trophy', 'Results', () => app.go('results')),
      iconButton('dr-btn dr-btn--ghost', 'back', 'Home', () => app.go('landing')),
    ),
  );

  async function toggleReminder(): Promise<void> {
    if (reminderEnabled()) {
      await disableDailyReminder();
      toast('Daily reminders off.');
    } else {
      const perm = await enableDailyReminder();
      if (perm === 'granted') toast("You're set — we'll remind you daily.");
      else if (perm === 'denied') toast('Reminders are blocked in your settings.');
      else if (perm === 'unsupported') toast('Reminders are not supported on this device.');
    }
    app.go('streak'); // re-render to reflect the new state
  }

  return {
    node: screen,
    dispose() {
      if (riskTimer) window.clearInterval(riskTimer);
    },
  };
}

/** A simple 7-cell strip ending today; the played days are filled. Because we
 *  only cache the last played day locally, we mark today + a contiguous tail of
 *  the streak as a friendly approximation (server holds the true history). */
function buildWeekStrip(_lastPlayed: string | null, today: string, playedToday: boolean): HTMLElement {
  const strip = div('dr-metrics');
  strip.style.gridTemplateColumns = 'repeat(7, 1fr)';
  const todayDate = new Date(`${today}T00:00:00Z`);
  for (let i = 6; i >= 0; i--) {
    const d = new Date(todayDate.getTime() - i * 86_400_000);
    const isToday = i === 0;
    const dim = d.toLocaleDateString('en-US', { weekday: 'narrow', timeZone: 'UTC' });
    const cell = div('dr-metric');
    cell.style.padding = '8px 4px';
    const filled = isToday ? playedToday : true; // approximate prior days as kept
    cell.append(
      el('div', { class: 'dr-metric__val', text: filled ? '🔥' : '·', style: { fontSize: '20px' } }),
      el('div', { class: 'dr-metric__label', text: dim }),
    );
    if (isToday && !playedToday) cell.style.outline = '2px solid var(--bad)';
    strip.append(cell);
  }
  return strip;
}
